import { useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import {
  AdminListCard,
  AdminListTable,
  EntitySlideout,
  saveErrorReason,
  saveErrorToast,
  type BatchAction,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { useServiceApprovers, useCollaboratorOptions } from "../api";
import type { ServiceApprover } from "../types";

interface ApproverOption {
  value: string;
  label: string;
}

const isValidApprover = (v: ApproverOption | null): boolean => v !== null;

export interface ApproversTabProps {
  lobId: string;
  // Approvers come from the parent Line of Business GET's `included`; onChanged
  // triggers that GET's refetch after an add/remove.
  approvers: ServiceApprover[];
  onChanged: () => void;
}

export const availableApproverOptions = (
  options: { value: string; label: string }[],
  takenUserIds: string[]
): { value: string; label: string }[] => {
  const taken = new Set(takenUserIds);
  return options.filter((o) => !taken.has(o.value));
};

export const ApproversTab = ({
  lobId,
  approvers,
  onChanged,
}: ApproversTabProps): JSX.Element => {
  const { add, remove } = useServiceApprovers(lobId);
  const { options } = useCollaboratorOptions();
  const sel = useBatchSelection([]);
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const available = availableApproverOptions(
    options,
    approvers.map((r) => r.userId)
  );

  const handleAdd = async (userId: string) => {
    try {
      await add(userId);
      setSlideoutOpen(false);
      onChanged();
    } catch (err) {
      setToast(saveErrorToast(err, "approver", "add"));
    }
  };

  const handleBatchDelete = async () => {
    const ids = [...sel.selectedIds];
    const failures: BatchFailure[] = [];
    for (const id of ids) {
      try {
        await remove(id);
      } catch (err) {
        failures.push({ reason: saveErrorReason(err) });
      }
    }
    if (failures.length > 0) {
      setToast({
        variant: "danger",
        text: withFailureReasons(
          `Failed to remove ${failures.length} of ${ids.length} approver(s).`,
          failures
        ),
      });
    }
    sel.clear();
    onChanged();
  };

  const batchActions: BatchAction[] = [
    {
      icon: faTrashCan,
      label: "Delete",
      onClick: handleBatchDelete,
      variant: "danger",
    },
  ];

  const columns: ColumnDef<ServiceApprover>[] = [
    {
      accessorKey: "name",
      header: "Approver",
      cell: ({ row }) => row.original.name,
    },
  ];

  return (
    <>
      <AdminListCard
        title="Approvers"
        bare
        isEmpty={approvers.length === 0}
        emptyMessage="No approvers yet."
        onNew={() => setSlideoutOpen(true)}
        newButtonLabel="Add Approver"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <AdminListTable
          columns={columns}
          data={approvers}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          emptyMessage="No approvers yet."
        />
      </AdminListCard>

      <EntitySlideout<ApproverOption | null>
        isOpen={slideoutOpen}
        initialValues={null}
        title="Add Approver"
        isValid={isValidApprover}
        onClose={() => setSlideoutOpen(false)}
        onSave={(selected) => handleAdd(selected!.value)}
      >
        {(selected, onChange) => (
          <RequiredReactSelect
            label="Add an approver"
            value={selected ?? undefined}
            onChange={(opt: any) => onChange(opt ?? null)}
            options={available}
            placeholder="Select a user…"
          />
        )}
      </EntitySlideout>

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ApproversTab;
